import { useEffect, useState } from "react";
import {
  EditProfileModal,
  ConfirmModal,
  OrderBox,
  OrderStatusModal,
  OrderDetailModal,
} from "../components";
import { useAuth, useStore } from "../hooks";
import {
  DeleteUserProfileApi,
  GetActiveOrders,
  GetUserApi,
  MarkOrderFavApi,
} from "../services";
import { useNavigate } from "react-router-dom";
import { Layout } from "../layouts";
import { Button } from "react-bootstrap";
import { CustomerModel, OrderModel } from "../models";
import Skeleton from "react-loading-skeleton";
import { Toast } from "../components/Toast";
import { MdOutlineEdit } from "react-icons/md";

const ProfilePage = () => {
  let navigate = useNavigate();
  const { storeId } = useStore();
  const { isLoggedIn, isGuest, customerId, logout } = useAuth();

  const [selectedOrder, setSelectedOrder] = useState<OrderModel>();

  const handleRefresh = () => {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customer, setCustomer] = useState<CustomerModel>();
  const [orders, setOrders] = useState<OrderModel[]>([]);
  const [activeModal, setActiveModal] = useState<
    "editProfile" | "deleteProfile" | "orderDetail" | "orderStatus" | null
  >(null);

  useEffect(() => {
    if (!isLoggedIn || isGuest) navigate("/login");

    fetchUser();
    fetchActiveOrders();
  }, [isLoggedIn, isGuest]);

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      const { status, message, data } = await GetUserApi({
        store_id: storeId,
        customer_id: customerId,
      });

      if (status) setCustomer(data);
      else Toast.error(message);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActiveOrders = async () => {
    const { status, data } = await GetActiveOrders({
      store_id: storeId,
      customer_id: customerId,
    });

    if (status) setOrders(data);
  };

  const handleDeleteProfile = async () => {
    const { status, message } = await DeleteUserProfileApi({
      store_id: storeId,
      customer_id: customerId,
    });

    if (status) {
      Toast.info(message);
      handleClose();
      logout();
      navigate("/");
    } else Toast.error(message);
  };

  const handleClose = () => {
    setActiveModal(null);
  };

  const handleFavOrder = async (order: OrderModel) => {
    try {
      const { status, data } = await MarkOrderFavApi({
        store_id: storeId,
        customer_id: customerId,
        order_id: order.order_id,
        is_fav: !order.is_favourite,
        is_fav_name: "",
      });

      if (status) {
        if (data.is_favourite) {
          const updatedOrder = { ...order, is_favourite: !order.is_favourite };
          setOrders((prevOrders) =>
            prevOrders.map((item) =>
              item.order_id === order.order_id ? updatedOrder : item,
            ),
          );
        }

        Toast.success(
          data.is_favourite
            ? "Order added to favorite!"
            : "Order removed from favorite!",
        );
      }
    } catch (error) {
      console.error("Error updating favourite status:", error);
    } finally {
      handleClose();
    }
  };

  return (
    <Layout showLoader={isLoading}>
      <div className="choose_payment_method_main dashbord_main">
        <div className="payment_header d-flex justify-content-between align-items-start button_meel_bottom mb-3">
          <div>
            <h2 className="page-heading mb-1">Your Profile</h2>
            <p className="mb-0 page-desc">Manage your profile details.</p>
          </div>
        </div>

        {!isLoading ? (
          <div className={`dashbord_sec1 card`}>
            <div className="profile-header">
              <div className="profile-header-left">
                <div className="profile-avatar">
                  {customer?.first_name?.charAt(0)}
                  {customer?.last_name?.charAt(0)}
                </div>

                <div>
                  <h2 className="page-heading mb-1">
                    {customer?.first_name} {customer?.last_name}
                  </h2>

                  <p>
                    {customer?.phone}
                    <span className="profile-separator"> • </span>
                    {customer?.email}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveModal("editProfile")}
                className="profile-edit-btn"
              >
                <MdOutlineEdit size={18} />
                Edit Profile
              </button>
            </div>

            {/* <h2 className="page-heading mb-1">
              {customer?.first_name?.toUpperCase()}{" "}
              {customer?.last_name?.toUpperCase()}
            </h2>
            <p>
              {customer?.phone} <span className="d-sm-inline d-none">|</span>{" "}
              <span className="d-block d-sm-inline"> {customer?.email} </span>{" "}
              <button
                onClick={() => setActiveModal("editProfile")}
                className="delete_btn icon-card"
              >
                <MdOutlineEdit size={20} className="icon-slate"/>
              </button>
            </p> */}
          </div>
        ) : (
          <Skeleton height={100} className="mb-3" />
        )}

        {orders.length > 0 && (
          <>
            <h3 className="fw-bolder pb-2">Active Orders</h3>
            {orders.map((order) => (
              <OrderBox
                key={order.order_id}
                order={order}
                onTrack={() => {
                  setSelectedOrder(order);
                  setActiveModal("orderStatus");
                }}
                onShow={() => {
                  setSelectedOrder(order);
                  setActiveModal("orderDetail");
                }}
              />
            ))}
          </>
        )}

        <div className="text-end mb-3">
          <Button
            variant="outline-danger"
            onClick={() => setActiveModal("deleteProfile")}
          >
            Delete My Account
          </Button>
        </div>
      </div>

      {customer && (
        <EditProfileModal
          show={activeModal === "editProfile"}
          handleClose={handleClose}
          customer={customer}
        />
      )}

      <ConfirmModal
        variant="danger"
        title="Delete my account"
        content="Are you sure you want to delete your account permanentally, this cannot be undone?"
        show={activeModal === "deleteProfile"}
        handleClose={handleClose}
        handleSubmit={handleDeleteProfile}
      />

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          show={activeModal === "orderDetail"}
          handleClose={handleClose}
          handleFavOrder={() => handleFavOrder(selectedOrder)}
        />
      )}

      {selectedOrder && (
        <OrderStatusModal
          order={selectedOrder}
          show={activeModal === "orderStatus"}
          handleClose={handleClose}
          onRefresh={handleRefresh}
        />
      )}
    </Layout>
  );
};

export default ProfilePage;
